import api from '../functions/api';
import getUserId from '../functions/getUserId';
import { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import LLMAnalysisBody, { parseAnalysis, TickerLinkProvider, TickerMarkdown } from './LLMAnalysisBody'; // DAS-3249: clickable tickers
import LLMCostLine, { costFromResponse } from './LLMCostLine'; // DAS-3044: one cost line, shared with the full-analysis view

function extractUrgency(raw) {
  const m = raw && raw.match(/Urgency Level:\s*([^\n]+)/i);
  return m ? m[1].trim() : null;
}

function urgencyColor(u) {
  const s = (u || '').toLowerCase();
  if (s.includes('very urgent')) return 'error';
  if (s.includes('urgent')) return 'warning';
  if (s.includes('mild')) return 'info';
  return 'default';
}

export default function Portfolio_LLM_analysis() {
  const [analysis, setAnalysis] = useState('');
  const [cost, setCost] = useState(null); // DAS-2505: LLM cost telemetry
  const [advice, setAdvice] = useState(null); // DAS-2841: structured advice record
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fetchLLMAnalysis = async () => {
      try {
        const userid = getUserId();
        const response = await api.get(`/portfolio_llm_analysis/${userid}`);
        setAnalysis(response.data.LLM_analysis || '');
        setCost(costFromResponse(response.data));
        setAdvice(response.data.advice || null);
      } catch (err) {
        setError('Failed to fetch analysis');
      } finally {
        setLoading(false);
      }
    };

    fetchLLMAnalysis();
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const { timestamp, sections } = parseAnalysis(analysis);
  // DAS-2841: urgency now comes from the stored record. The regex over the
  // prose stays only as a fallback for analyses written before this shipped —
  // one source of truth, with the old path kept so nothing goes blank.
  const urgency =
    advice && advice.urgency && advice.urgency !== 'None'
      ? advice.urgency
      : extractUrgency(analysis);
  const transaction = sections.find((s) => s.title.toLowerCase().includes('transaction'));

  // DAS-2522/DAS-3044: the provider split and the line itself live in LLMCostLine.

  return (
    <>
      <Box
        onClick={handleOpen}
        data-testid="portfolio-llm-analysis-button"
        sx={{
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 'bold',
          padding: '0.8px',
          width: 'fit-content'
        }}
      >
        LLM Portfolio Analysis
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          LLM Portfolio Analysis
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '75vh' }}>
          {loading && <CircularProgress />}
          {error && <Typography color="error">{error}</Typography>}

          {!loading && !error && (
            <TickerLinkProvider extraTickers={[advice?.sell_ticker, advice?.buy_ticker]}>
              {/* Meta: run time + models + cost + tokens (DAS-2505, DAS-3044) */}
              <LLMCostLine cost={cost} timestamp={timestamp} style={{ marginBottom: 12 }} />

              {/* Surfaced actionable recommendation */}
              {(transaction || advice) && (
                <Box sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'primary.main', borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>📌 Recommended action</Typography>
                    {urgency && (
                      <Chip
                        size="small"
                        color={urgencyColor(urgency)}
                        label={urgency}
                        data-testid="portfolio-advice-urgency"
                      />
                    )}
                  </Box>

                  {/* DAS-2841: the stored record — the same advice as data, so what
                      is measured is visible next to what is read. */}
                  {advice && (
                    <Box
                      data-testid="portfolio-advice-record"
                      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, fontSize: '12.5px', color: 'text.secondary' }}
                    >
                      {advice.action && <span><strong>{advice.action}</strong></span>}
                      {advice.sell_ticker && <span>· sell {advice.sell_ticker}</span>}
                      {advice.buy_ticker && <span>· buy {advice.buy_ticker}</span>}
                      {advice.amount_eur != null && <span>· €{Number(advice.amount_eur).toLocaleString()}</span>}
                      {advice.horizon && <span>· {advice.horizon === 'short' ? 'short term' : 'long term'}</span>}
                      {advice.followed_up_alert_id != null && (
                        <span>· follows up alert #{advice.followed_up_alert_id}</span>
                      )}
                      {advice.agrees_with_swap_engine != null && (
                        <span>· {advice.agrees_with_swap_engine ? 'agrees with' : 'differs from'} Swap Engine</span>
                      )}
                      {advice.parse_status && advice.parse_status !== 'ok' && (
                        <span>· record incomplete ({advice.parse_status})</span>
                      )}
                    </Box>
                  )}

                  {transaction && (
                    <Box sx={{ fontSize: '13px', '& p': { mt: 0.5, mb: 0.5 } }}>
                      <TickerMarkdown content={transaction.content} />
                    </Box>
                  )}
                </Box>
              )}

              {/* DAS-2904: final corrected advice on top, the debate folded away.
                  The recommended-action box above keeps the transaction, so the
                  body only renders the final advice + debate (legacy blobs fall
                  back to raw text inside the body). */}
              <LLMAnalysisBody analysis={analysis} />
            </TickerLinkProvider>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
